import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

/**
 * Generate time options every 30 minutes from 08:00 to 18:00
 * Returns an array of strings in "HH:mm" format
 * @param {string} selectedDate - Optional date string in 'YYYY-MM-DD' format. If provided and is today, filters out past times.
 */
export function getTimeOptions(selectedDate = null) {
  const options = [];
  const now = dayjs();
  const isToday = selectedDate && dayjs(selectedDate).isSame(now, 'day');
  const currentTime = now.hour() * 60 + now.minute(); // Current time in minutes
  
  for (let mins = 8 * 60; mins <= 18 * 60; mins += 30) {
    // If it's today, skip times that have already passed
    if (isToday && mins <= currentTime) {
      continue;
    }
    const hour = Math.floor(mins / 60);
    const minute = mins % 60 === 0 ? '00' : '30';
    options.push(`${hour.toString().padStart(2, '0')}:${minute}`);
  }
  return options;
}

/**
 * Format a 24-hour time string ("HH:mm") into a 12-hour display with am/pm
 */
export function formatAmPm(time24) {
  const [h, m] = time24.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
}

/**
 * Date picker that disables weekends (Saturday and Sunday) and past dates
 */
export function LimitedDatePicker({ value, onChange }) {
  const today = dayjs().startOf('day');
  
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label="Date"
        value={value ? dayjs(value) : null}
        onChange={(newValue) => {
          if (newValue) {
            onChange(newValue.format('YYYY-MM-DD'));
          }
        }}
        minDate={today}
        shouldDisableDate={(day) => {
          // Disable weekends: 0 = Sunday, 6 = Saturday
          if (day.day() === 0 || day.day() === 6) {
            return true;
          }
          // Disable past dates (minDate should handle this, but adding as extra check)
          if (day.isBefore(today, 'day')) {
            return true;
          }
          return false;
        }}
        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
      />
    </LocalizationProvider>
  );
}

LimitedDatePicker.propTypes = {
  value: PropTypes.string.isRequired, // in 'YYYY-MM-DD' format
  onChange: PropTypes.func.isRequired,
};

/**
 * Time picker that restricts times between 08:00 and 18:00
 */
export function LimitedTimePicker({ value, onChange }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        label="Time"
        value={value ? dayjs(value, 'HH:mm') : null}
        onChange={(newValue) => {
          if (newValue) {
            onChange(newValue.format('HH:mm'));
          }
        }}
        minTime={dayjs().hour(8).minute(0)}
        maxTime={dayjs().hour(18).minute(0)}
        renderInput={(params) => <TextField {...params} size="small" fullWidth />}
      />
    </LocalizationProvider>
  );
}

LimitedTimePicker.propTypes = {
  value: PropTypes.string.isRequired, // in 'HH:mm' format
  onChange: PropTypes.func.isRequired,
};
